import React from 'react';
import { ToastContainer as ToastifyContainer } from 'react-toastify';
import type { TypeOptions } from 'react-toastify';
import classNames from 'classnames';

import Icons from 'assets/icons';

interface ToastStyleConfig {
  closeButton: string;
  component: string;
  progressBar: string;
  body: string;
}

const toastStyles: ToastStyleConfig = {
  component: 'rounded-md p-4 border-t border-r border-navy-100 !shadow-toast',
  progressBar: 'rounded-bl-none',
  closeButton: 'text-gray-600',
  body: 'items-start pl-3 pr-0',
};

const toastTypeStyles = {
  error: {
    component: `text-critical-700 border-l-8 border-l-critical-700 ${toastStyles.component}`,
    closeButton: toastStyles.closeButton,
    progressBar: `bg-critical-700/50 ${toastStyles.progressBar}`,
  },
  info: {
    component: `text-navy-800 border-l-8 border-l-navy-800 ${toastStyles.component}`,
    closeButton: toastStyles.closeButton,
    progressBar: `bg-navy-800/50 ${toastStyles.progressBar}`,
  },
  success: {
    component: `text-lime-700 border-l-8 border-l-lime-600 ${toastStyles.component}`,
    closeButton: toastStyles.closeButton,
    progressBar: `bg-lime-200 ${toastStyles.progressBar}`,
  },
  warning: {
    component: `text-amber-500 border-l-8 border-l-amber-500 ${toastStyles.component}`,
    closeButton: toastStyles.closeButton,
    progressBar: `bg-amber-500/50 ${toastStyles.progressBar}`,
  },
  default: {
    component: `text-gray-700 border-l-8 border-l-gray-700 ${toastStyles.component}`,
    closeButton: toastStyles.closeButton,
    progressBar: `bg-gray-700/50 ${toastStyles.progressBar}`,
  },
};

type ToastStyleKey = 'component' | 'closeButton' | 'progressBar';

const getToastTypeStyles = (
  defaultClassName = '',
  type: TypeOptions = 'default',
  key: ToastStyleKey
): string =>
  classNames(
    defaultClassName,
    toastTypeStyles[type]?.[key] ?? toastTypeStyles.default[key]
  );

interface CustomCloseButtonProps {
  closeToast?: (e: React.MouseEvent<HTMLElement>) => void;
  type?: TypeOptions;
}

const CustomCloseButton = ({
  closeToast,
  type,
}: CustomCloseButtonProps): React.JSX.Element => {
  const closeButtonClass = getToastTypeStyles('', type, 'closeButton');

  return (
    <button aria-label="Close notification" onClick={closeToast}>
      <Icons.Close className={closeButtonClass} />
    </button>
  );
};

const ToastContainer = (): React.JSX.Element => (
  <ToastifyContainer
    style={
      {
        '--toastify-color-progress-bgo': '0',
        '--toastify-font-family':
          "'Barlow', 'IBM Plex Sans', system-ui, sans-serif",
        '--toastify-toast-width': '384px',
        '--toastify-toast-right': '20px',
      } as React.CSSProperties
    }
    autoClose={2500}
    draggable
    position="top-right"
    hideProgressBar={false}
    pauseOnHover
    closeOnClick={false}
    toastClassName={(toast) =>
      getToastTypeStyles(toast?.defaultClassName, toast?.type, 'component')
    }
    bodyClassName={toastStyles.body}
    closeButton={(props) => (
      <CustomCloseButton closeToast={props.closeToast} type={props.type} />
    )}
    progressClassName={(toast) =>
      getToastTypeStyles(toast?.defaultClassName, toast?.type, 'progressBar')
    }
  />
);

export default ToastContainer;
